import { ITEM_TYPES_MAP } from "@shared/constants/enums";
import { ItemTypesMapIdType } from "@shared/constants/enums/enums-types";
import gtagManager from "./loader/gtag";
import { AnalyticEventTypes, BaseAnalyticsType } from "./types/analytics-types";
import {
  addToCartAnalyticsData,
  beginCheckoutAnalyticsData,
  GoogleEventNames,
  postPurchaseAnalyticsData,
  removeFromCartAnalyticsData,
  viewItemsAnalyticsData,
} from "./utils/google";

// Returns a string representing the item type to be tracked, plus the item's id
const getTrackingId = (
  id: number | string,
  itemType: ItemTypesMapIdType | undefined
): string => {
  let item_id = "";

  switch (itemType) {
    case ITEM_TYPES_MAP.ITEM_TYPE_PRODUCT.id:
      item_id = `pa-${id}`;
      break;
    case ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id:
      item_id = `ml-${id}`;
      break;
    case ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id:
      item_id = `tt-${id}`;
      break;
    default:
      item_id = "";
      break;
  }

  return item_id;
};

// Returns a string representing the category of the item type to be tracked
const getTrackingCategory = (
  itemType: ItemTypesMapIdType | undefined
): string => {
  let item_category = "";

  switch (itemType) {
    case ITEM_TYPES_MAP.ITEM_TYPE_PRODUCT.id:
      item_category = "products";
      break;
    case ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id:
      item_category = "memberships";
      break;
    case ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id:
      item_category = "events";
      break;
    default:
      item_category = "";
      break;
  }

  return item_category;
};

const isGtagLoaded = () => {
  return (
    typeof window !== "undefined" &&
    window &&
    window.dataLayer instanceof Array &&
    typeof window.gtag === "function"
  );
};

export interface GAAnalyticsType extends BaseAnalyticsType {
  viewItems: AnalyticEventTypes["viewItems"];
  viewContent: AnalyticEventTypes["viewContent"];
  removeFromCart: AnalyticEventTypes["removeFromCart"];
  awConversion: AnalyticEventTypes["awConversion"];
  formatTrackingData: AnalyticEventTypes["formatGtagTrackingData"];
  disable: (id: string) => void;
  disableAll: () => void;
}

const gtag: GAAnalyticsType = {
  init(id) {
    gtagManager.loadGTag(id);
  },

  disable(id) {
    gtagManager.disableGTag(id);
  },

  disableAll() {
    gtagManager.disableAllTags();
  },

  pageView() {
    // Automatically done by Google via Enhanced Event Measurement, provided an organizer has enabled it
  },

  viewContent(item) {
    if (!isGtagLoaded()) return;

    gtagManager.push("event", GoogleEventNames.ViewContent, {
      content_ids: [item.id],
      content_type: item.type ?? "product_group",
      content_name: item.name,
    });
  },

  viewItems(ttset, venue) {
    if (!isGtagLoaded()) return;

    const viewItemsData = viewItemsAnalyticsData(
      ttset,
      venue?.name,
      venue?.currency
    );
    gtagManager.push("event", GoogleEventNames.ViewItem, viewItemsData);
  },

  addToCart(group, count, venue) {
    if (!isGtagLoaded()) {
      return;
    }

    const addToCartData = addToCartAnalyticsData(
      group,
      count,
      venue?.name,
      venue?.currency
    );
    gtagManager.push("event", GoogleEventNames.AddToCart, addToCartData);
  },

  removeFromCart() {
    if (!isGtagLoaded()) return;

    const removeFromCartData = removeFromCartAnalyticsData();
    gtagManager.push(
      "event",
      GoogleEventNames.RemoveFromCart,
      removeFromCartData
    );
  },

  beginCheckout(basket, venue) {
    if (!isGtagLoaded()) return;

    const beginCheckoutData = beginCheckoutAnalyticsData(
      basket,
      venue?.name,
      venue?.currency
    );
    gtagManager.push(
      "event",
      GoogleEventNames.BeginCheckout,
      beginCheckoutData
    );
  },

  postPurchase(basket, venue) {
    if (!isGtagLoaded()) return;

    const postPurchaseData = postPurchaseAnalyticsData(
      basket,
      venue?.name,
      venue?.currency
    );
    gtagManager.push("event", GoogleEventNames.PostPurchase, postPurchaseData);
  },

  awConversion(conversionID, conversionLabel, item, ...args) {
    if (!isGtagLoaded()) return;

    gtagManager.push("event", GoogleEventNames.AWConversion, {
      send_to: `${conversionID}/${conversionLabel}`,
      ...item,
      ...args,
    });
  },

  grantConsent() {
    if (!isGtagLoaded()) return;

    // TODO offer granularity based on consent
    gtagManager.push("consent", "update", {
      ad_storage: "granted",
      analytics_storage: "granted",
    });
  },

  revokeConsent() {
    if (!isGtagLoaded()) return;

    gtagManager.push("consent", "update", {
      ad_storage: "denied",
      analytics_storage: "denied",
    });
  },

  track(_, __, action, label) {
    if (!isGtagLoaded()) return;

    gtagManager.push("event", action as string, {
      event_label: label,
    });
  },

  formatTrackingData(
    id, // id of the ticket_type, membership_level, or product_attribute
    name, // name of the event, membership_group, or product
    variant, // name of the ticket_type, membership_level, or product_attribute
    itemType // "ticket" or "product" or "membership"
  ) {
    return {
      item_num_id: !Number.isNaN(Number(id)) ? Number(id) : -1, // temporary field to match on basket item_groups
      item_id: getTrackingId(id, itemType),
      item_category: getTrackingCategory(itemType),
      item_name: name,
      item_variant: variant,
      item_type: itemType,
    };
  },
};

export default gtag;
